/**
 * Predictive search (replaces Impulse's PredictiveSearch)
 *
 * Impulse's version was functional but unusable by keyboard: the markup declared
 * `role="listbox"` / `role="option"`, yet the only key it handled was Escape — so the panel
 * announced itself as a navigable listbox that could not actually be navigated. It also left
 * `aria-expanded="false"` while open, never set `aria-activedescendant`, bound listeners with no
 * way to remove them, and re-threw its fetch rejection so a failed search looked identical to
 * "no results".
 *
 * This owns the whole component instead. It keeps Impulse's event contract so the existing header
 * plumbing still works:
 *   - listens for `predictive-search:open` (matching `data-context`) and `predictive-search:close`
 *   - dispatches `predictive-search:close-all` on close, which theme.js listens for to tear down
 *     the header search drawer. Dropping that would leave the drawer stuck open.
 *
 * Keyboard model is the combobox + activedescendant pattern: focus never leaves the input, the
 * arrow keys move which option is "active", and the input's `aria-activedescendant` points at it.
 * Options are `tabindex="-1"` so they're never in the tab order.
 */
const PREDICTIVE_SEARCH_DEBOUNCE_MS = 300;
// Shopify's per-resource maximum, deliberately deeper than the three results the panel shows.
// Relevance ranking knows nothing about our tag-driven discontinued state, so a term like "max
// trainer" can return three discontinued machines ahead of the live one. search-results.liquid
// sorts live products ahead of discontinued ones, but it can only reorder what this fetch
// returned — asking for exactly three left it nothing live to promote. The panel caps what it
// renders back to three; that cap lives in search-results.liquid, so the two move together.
const PREDICTIVE_SEARCH_FETCH_LIMIT = 10;
// Keys the listbox takes over from the input, but only while there are options to move between.
const NAVIGATION_KEYS = ['ArrowDown', 'ArrowUp', 'Home', 'End'];

class BfxPredictiveSearch extends HTMLElement {
  connectedCallback() {
    this.context = this.dataset.context;
    this.enabled = this.dataset.enabled === 'true';
    this.input = this.querySelector('input[type="search"]');
    this.panel = this.querySelector('.search__results');
    this.screen = this.querySelector('[data-screen]');
    this.closeBtn = this.querySelector('.btn--close-search');

    if (!this.input || !this.panel) return;

    this.activeIndex = -1;
    this.latestRequestId = 0;
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    // Opened from elsewhere (the header search button dispatches this).
    document.addEventListener(
      'predictive-search:open',
      (event) => {
        if (event.detail?.context !== this.context) return;
        this.reveal();
      },
      { signal }
    );

    document.addEventListener('predictive-search:close', () => this.close(), { signal });

    this.screen?.addEventListener('click', () => this.close(), { signal });

    this.closeBtn?.addEventListener(
      'click',
      (event) => {
        event.preventDefault();
        this.close();
      },
      { signal }
    );

    this.input.addEventListener('keydown', (event) => this.onKeydown(event), { signal });

    // Close when focus moves to something outside the component (tabbing out of a combobox
    // dismisses it). A null relatedTarget means focus left the document altogether — switching
    // tabs or windows — which must NOT close the panel, or the shopper loses their results just
    // for glancing at another window.
    this.addEventListener(
      'focusout',
      (event) => {
        if (event.relatedTarget && !this.contains(event.relatedTarget)) this.close();
      },
      { signal }
    );

    // Clicking an option is a plain link navigation — nothing to intercept.
    if (!this.enabled) return;

    // Held on the instance so close() can cancel a pending run — see close().
    this.onInput = this.debounce(() => this.onChange());
    this.input.addEventListener('input', this.onInput, { signal });
  }

  disconnectedCallback() {
    this.abortController?.abort();
    // The controller unbinds listeners but can't stop a timer that's already scheduled, which would
    // otherwise fire onChange() against a detached panel.
    this.onInput?.cancel();
  }

  // ── Options ─────────────────────────────────────────────────────────────────

  get options() {
    return [...this.panel.querySelectorAll('[role="option"]')];
  }

  /**
   * Mark one option active. Passing -1 clears the selection, which is the state the panel opens
   * in — nothing is preselected, so Enter submits the typed query rather than a suggestion.
   */
  setActive(index) {
    const options = this.options;
    this.activeIndex = index;

    options.forEach((option, i) => {
      option.setAttribute('aria-selected', i === index ? 'true' : 'false');
      option.classList.toggle('is-active', i === index);
    });

    const active = options[index];
    if (active) {
      this.input.setAttribute('aria-activedescendant', active.id);
      // The panel scrolls when results overflow, so keep the active option in view.
      active.scrollIntoView({ block: 'nearest' });
    } else {
      this.input.removeAttribute('aria-activedescendant');
    }
  }

  moveActive(step) {
    const count = this.options.length;
    if (!count) return;
    // Wrap around, treating -1 (nothing active) as "before the first option".
    const next = (this.activeIndex + step + count + 1) % (count + 1);
    this.setActive(next === count ? -1 : next);
  }

  // ── Keyboard ────────────────────────────────────────────────────────────────

  onKeydown(event) {
    if (event.key === 'Escape') {
      this.close();
      this.returnFocus();
      return;
    }

    if (!this.isOpen) return;

    // The panel can be open with nothing to navigate — the no-results state and the failure notice
    // both render without options. Claiming these keys there swallowed the input's own caret
    // controls, so Home wouldn't jump to the start of the query the shopper was still editing.
    if (NAVIGATION_KEYS.includes(event.key) && !this.options.length) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.moveActive(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.moveActive(-1);
        break;
      case 'Home':
        event.preventDefault();
        this.setActive(0);
        break;
      case 'End':
        event.preventDefault();
        this.setActive(this.options.length - 1);
        break;
      case 'Enter': {
        const active = this.options[this.activeIndex];
        // With nothing active, let the form submit and run the full search.
        if (!active) return;
        event.preventDefault();
        window.location.href = active.href;
        break;
      }
      default:
        break;
    }
  }

  // ── Fetching ────────────────────────────────────────────────────────────────

  onChange() {
    const term = this.input.value.trim();
    if (!term.length) {
      this.close();
      return;
    }
    this.getSearchResults(term);
  }

  async getSearchResults(term) {
    const endpoint = window.theme?.routes?.predictiveSearch;
    // No route means the theme globals never initialised — leave the form to do a full search.
    if (!endpoint) return;

    // Responses can land out of order, so a slower request for an earlier term could overwrite the
    // results for a newer one and leave the panel showing the wrong thing mid-typing. Stamp each
    // request and let only the newest one write to the panel.
    const requestId = ++this.latestRequestId;

    const params = new URLSearchParams({
      q: term,
      'resources[limit]': PREDICTIVE_SEARCH_FETCH_LIMIT,
      'resources[limit_scope]': 'each',
      section_id: 'search-results',
    });

    try {
      const response = await fetch(`${endpoint}?${params}`);
      if (!response.ok) throw new Error(`Predictive search responded ${response.status}`);

      const markup = new DOMParser()
        .parseFromString(await response.text(), 'text/html')
        .querySelector('#shopify-section-search-results');
      if (!markup) throw new Error('Predictive search section missing from the response');

      if (requestId !== this.latestRequestId) return;

      // Source is Shopify's own section render, so this markup is trusted.
      this.panel.innerHTML = markup.innerHTML;
      this.open();
    } catch (error) {
      // A superseded request failing must not replace a newer request's results with the error
      // notice, so it's dropped here too — still logged, just not rendered.
      if (requestId !== this.latestRequestId) {
        console.error('[bfx-predictive-search] superseded request failed', error);
        return;
      }

      // Don't fail silently: say so in the panel and log it. The form still submits, so the
      // shopper can always run the full search regardless.
      const message = window.theme?.strings?.searchError;
      if (message) {
        this.panel.textContent = '';
        // No id wrapper needed: `aria-controls` targets the panel itself, which is always present.
        const notice = document.createElement('p');
        notice.className = 'predictive-search__error';
        notice.textContent = message;
        this.panel.append(notice);
        this.open();
      } else {
        this.close();
      }
      console.error('[bfx-predictive-search]', error);
    }
  }

  // ── Open / close ────────────────────────────────────────────────────────────

  get isOpen() {
    return this.input.getAttribute('aria-expanded') === 'true';
  }

  /** Show the component's overlay + field without waiting for results (used by the header). */
  reveal() {
    this.classList.add('is-active');
    document.body.classList.add('predictive-overflow-hidden');
    // The header animates the field in; focus once that's had a frame to settle.
    requestAnimationFrame(() => this.input.focus());
  }

  open() {
    this.classList.add('is-active');
    document.body.classList.add('predictive-overflow-hidden');
    this.panel.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
    this.setActive(-1);

    // Unrolls the panel (clip-path, matching the mega menu). The class has to land a frame after
    // unhiding: a display change can't be transitioned, so the browser needs to paint the closed
    // clip first or the reveal is skipped. Skipped entirely if it's already open, so re-fetching on
    // each keystroke doesn't replay the animation.
    if (!this.panel.classList.contains('is-open')) {
      requestAnimationFrame(() => this.panel.classList.add('is-open'));
    }
  }

  /**
   * Put focus somewhere sensible after an explicit dismissal (Escape).
   *
   * Closing dispatches `predictive-search:close-all`, which makes theme.js tear the header search
   * drawer down — so by this point the header's input can be hidden, and focusing a hidden element
   * silently does nothing, dropping the user at the top of the document. Prefer the input when it's
   * still visible (the search results page, where it always is), otherwise hand focus back to the
   * control that opened the search.
   */
  returnFocus() {
    if (this.input.offsetParent !== null) {
      this.input.focus();
      return;
    }
    document.querySelector('.js-search-header')?.focus();
  }

  close() {
    // Retires any in-flight request so a response that lands after this can't reopen the panel the
    // shopper just dismissed — clearing the field fires this path on every keystroke back to empty.
    this.latestRequestId += 1;

    // Drops a debounce that hasn't run yet, for the same reason. Dismissing inside the 300ms window
    // (Escape, the close button, clicking the overlay) otherwise let onChange() fire afterwards and
    // re-fetch — and on the results page, where the query is deliberately left in the field, the
    // panel reopened on its own a moment after the shopper closed it.
    this.onInput?.cancel();

    this.panel.classList.remove('is-open');
    this.panel.hidden = true;
    this.panel.innerHTML = '';

    // Closing the header search dismisses the whole thing — the field collapses with it — so the
    // query is cleared and reopening starts fresh. NOT on the results page: there the field holds
    // the query that produced the page behind it, and wiping it would misrepresent what's shown.
    if (this.context !== 'search-page') {
      this.input.value = '';
    }

    this.input.setAttribute('aria-expanded', 'false');
    this.input.removeAttribute('aria-activedescendant');
    this.activeIndex = -1;
    this.classList.remove('is-active');
    document.body.classList.remove('predictive-overflow-hidden');

    // theme.js listens for this to close the header search drawer — and its handler calls a bare
    // `document.activeElement.blur()`. close() also runs whenever the query is emptied (see
    // onChange), so firing this unconditionally stole focus out of the results-page field the moment
    // a shopper backspaced their query, leaving it dead until they clicked back in. The event exists
    // only to tear the drawer down, so it's gated on the drawer actually being open — theme.js marks
    // that with `js-drawer-open--search` on <html>.
    if (document.documentElement.classList.contains('js-drawer-open--search')) {
      document.dispatchEvent(new CustomEvent('predictive-search:close-all'));
    }
  }

  // ── Utilities ───────────────────────────────────────────────────────────────

  /** Exposes `cancel()` so a scheduled run can be dropped when the panel closes. */
  debounce(fn, wait = PREDICTIVE_SEARCH_DEBOUNCE_MS) {
    let timer;
    const debounced = (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), wait);
    };
    debounced.cancel = () => clearTimeout(timer);
    return debounced;
  }
}

// Loaded once from layout/theme.liquid, so this can't double-register today. Kept as a guard
// because the snippet renders twice per page (header + search results): if the tag ever moves back
// into it, re-registering throws NotSupportedError.
if (!customElements.get('bfx-predictive-search')) {
  customElements.define('bfx-predictive-search', BfxPredictiveSearch);
}
